import type { ChangeEvent } from 'react'
import { AzureOfferPlan } from '../../../modules/privateOffer/types'
import clsx from 'clsx'
import { useStyles } from './AzurePricingSection.styles'
import { Typography } from '@mui/material'
import { Checkbox, TextInput } from '@labrav/react-components'
import { isEmpty } from 'lodash'
import { getValidValueBetweenMinMax } from '@labrav/common'

export interface AzureUserMinMaxProps {
  planRow: AzureOfferPlan
  index: number
  updatePlanId: (
    index: number,
    plan: AzureOfferPlan,
    keyForErrorCheck?: string,
    subIndex?: number,
    meter?: string
  ) => Promise<void>
  planRowError: Record<string, unknown>
}

export const AzureUserMinMax = ({
  index,
  planRow,
  planRowError,
  updatePlanId,
}: AzureUserMinMaxProps) => {
  const { classes } = useStyles()
  const userLimitsData = { ...planRow.pricing.recurrentPrice.userLimits }
  const nonEditedUserLimitsData = {
    ...planRow.originalPricing?.recurrentPrice.userLimits,
  }
  const userLimitErr = {
    ...(planRowError.userLimits as Record<string, unknown>),
  }

  const onChange =
    (keyName: 'min' | 'max' | 'minEnable' | 'maxEnable') =>
    (event: ChangeEvent<HTMLInputElement>, checked?: boolean) => {
      const recurrentPriceData = {
        ...planRow.pricing.recurrentPrice,
        userLimits: {
          ...planRow.pricing.recurrentPrice.userLimits,
          [keyName]: ['minEnable', 'maxEnable'].includes(keyName)
            ? checked
            : getValidValueBetweenMinMax(
                event.target.value as string,
                1,
                1000000
              ),
        },
      }
      const updateRowData = {
        ...planRow,
        pricing: {
          ...planRow.pricing,
          recurrentPrice: {
            ...recurrentPriceData,
          },
        },
      }

      updatePlanId(
        index,
        {
          ...updateRowData,
        },
        keyName
      )
    }

  return (
    <div>
      <div
        className={clsx(
          classes.subHeadingContainer,
          classes.companyDetailSubHead,
          classes.metering
        )}
      >
        <Typography
          className={classes.subHeading}
          data-testid="min-max-heading"
        >
          Min-max users
        </Typography>
      </div>

      <UserMinMaxRow
        inputKeyName={'min'}
        checkKeyName={'minEnable'}
        checkValue={userLimitsData.minEnable}
        inputValue={
          userLimitsData.minEnable
            ? userLimitsData.min
            : nonEditedUserLimitsData.min
        }
        inputErr={(userLimitErr?.min || '') as string}
        displayText={'Minimum users'}
        onChange={onChange}
      />
      <UserMinMaxRow
        inputKeyName={'max'}
        checkKeyName={'maxEnable'}
        checkValue={userLimitsData.maxEnable}
        inputValue={
          userLimitsData.maxEnable
            ? userLimitsData.max
            : nonEditedUserLimitsData.max
        }
        inputErr={(userLimitErr?.max || '') as string}
        displayText={'Maximum users'}
        onChange={onChange}
      />
    </div>
  )
}

export const UserMinMaxRow = ({
  inputKeyName,
  checkKeyName,
  checkValue,
  inputValue,
  inputErr,
  displayText,
  onChange,
}: {
  inputKeyName: 'min' | 'max'
  checkKeyName: 'minEnable' | 'maxEnable'
  displayText: string
  checkValue?: boolean
  inputValue?: number
  inputErr?: string
  onChange: (
    checkKeyName: 'min' | 'max' | 'minEnable' | 'maxEnable'
  ) => (event: ChangeEvent<HTMLInputElement>, checked?: boolean) => void
}) => {
  const { classes } = useStyles()
  return (
    <div className={classes.userMinMaxRow} data-testid={'min-max-row'}>
      <Checkbox
        defaultChecked={checkValue}
        color="default"
        className={classes.checkboxLeft}
        checked={Boolean(checkValue)}
        name={checkKeyName}
        id={checkKeyName}
        onChange={onChange(checkKeyName)}
        inputProps={
          {
            'data-testid': 'checkbox-item',
          } as never
        }
      />
      <p className={classes.userLabel}>{displayText}</p>
      <div className={classes.userInput}>
        <TextInput
          type="number"
          title={displayText}
          placeHolder={displayText}
          errorText={inputErr as string}
          isError={!isEmpty(inputErr || '')}
          isReadOnly={!checkValue}
          isRequired={true}
          value={inputValue || ''}
          updatedValue={inputValue || ''}
          onChange={onChange(inputKeyName)}
          keyName={inputKeyName}
          minNumber={1}
          maxNumber={1000000}
          variant="regularV2"
        />
      </div>
    </div>
  )
}
